import styled from 'styled-components'
import { ProgramChange, UpdatedUserRecord, UserFieldChange } from 'Services/apiResponseTypes'
import { spacingSM } from 'Utils/styles/spacing'
import { elfGreen, frostedGlass, red, towerGray, zirconGray } from 'Utils/styles/colors'

const TableContainer = styled.div`
  overflow-y: auto;
  border: 1px solid ${zirconGray};
  border-radius: 4px;
  max-height: calc(80vh - 450px);
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Thead = styled.thead`
  position: sticky;
  top: 0;
  background-color: ${frostedGlass};
  z-index: 1;
`

const TheadTr = styled.tr`
  box-shadow: 0 2px 2px -2px ${towerGray};
`

const Th = styled.th`
  padding: ${spacingSM};
  text-align: left;
  white-space: nowrap;
`

const Tr = styled.tr`
  &:nth-child(even) {
    background-color: ${frostedGlass};
  }

  &:hover {
    background-color: ${zirconGray};
  }
`

const Td = styled.td`
  padding: ${spacingSM};
  border-bottom: 1px solid ${zirconGray};
  max-width: 250px;
  overflow-wrap: break-word;
  vertical-align: top;
`

const ChangesList = styled.div`
  font-size: 12px;
`

const ChangeItem = styled.div`
  margin-bottom: 4px;
  line-height: 1.4;
`

const FieldName = styled.span`
  font-weight: 600;
  color: ${towerGray};
`

const ChangeValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
`

const OldValue = styled.div`
  background-color: #ffebee;
  color: ${red};
  padding: 2px 6px;
  text-decoration: line-through;
  border-radius: 2px;
  font-size: 11px;
`

const NewValue = styled.div`
  background-color: #e8f5e9;
  color: ${elfGreen};
  padding: 2px 6px;
  border-radius: 2px;
  font-size: 11px;
`

const EmptyPlaceholder = styled.span`
  font-style: italic;
  color: ${towerGray};
  font-size: 11px;
`

const EmptyState = styled.div`
  padding: ${spacingSM};
  text-align: center;
  color: ${towerGray};
  font-style: italic;
`

const ProgramAdd = styled.div`
  background-color: #e8f5e9;
  color: ${elfGreen};
  padding: 4px 8px;
  border-radius: 2px;
  font-size: 11px;
  margin-bottom: 4px;
`

const ProgramRemove = styled.div`
  background-color: #ffebee;
  color: ${red};
  padding: 4px 8px;
  text-decoration: line-through;
  border-radius: 2px;
  font-size: 11px;
  margin-bottom: 4px;
`

const ProgramUpdate = styled.div`
  font-size: 11px;
  margin-bottom: 4px;
`

type UpdatesTabProps = {
  users: UpdatedUserRecord[]
}

export const UpdatesTab = ({ users }: UpdatesTabProps) => {
  if (users.length === 0) {
    return <EmptyState>No users to update</EmptyState>
  }

  const renderFieldChanges = (userFieldChanges?: Record<string, UserFieldChange<any>>) => {
    if (!userFieldChanges || Object.keys(userFieldChanges).length === 0) {
      return '-'
    }

    return (
      <ChangesList>
        {Object.entries(userFieldChanges).map(([field, change]) => (
          <ChangeItem key={field}>
            <FieldName>{field}:</FieldName>
            <ChangeValue>
              <OldValue>
                {change.old_value ?? <EmptyPlaceholder>&lt;empty&gt;</EmptyPlaceholder>}
              </OldValue>
              <NewValue>
                {change.new_value ?? <EmptyPlaceholder>&lt;empty&gt;</EmptyPlaceholder>}
              </NewValue>
            </ChangeValue>
          </ChangeItem>
        ))}
      </ChangesList>
    )
  }

  const renderProgramChanges = (programChanges?: ProgramChange[]) => {
    if (!programChanges || programChanges.length === 0) {
      return '-'
    }

    return (
      <ChangesList>
        {programChanges.map((change, idx) => {
          if (change.operation === 'add') {
            return (
              <ProgramAdd key={idx}>
                ➕ {change.program_name}
                {change.start_date && ` (Start: ${change.start_date})`}
                {change.end_date && ` (End: ${change.end_date})`}
                {change.allowance_mod && ` - Allowance: ${change.allowance_mod}`}
              </ProgramAdd>
            )
          }

          if (change.operation === 'remove') {
            return <ProgramRemove key={idx}>➖ {change.program_name}</ProgramRemove>
          }

          const updates: string[] = []
          if (change.start_date && typeof change.start_date === 'object') {
            updates.push(`Start: ${change.start_date.old_value} → ${change.start_date.new_value}`)
          }
          if (change.end_date && typeof change.end_date === 'object') {
            const oldValue = change.end_date.old_value || 'Ongoing'
            const newValue = change.end_date.new_value || 'Ongoing'
            updates.push(`End: ${oldValue} → ${newValue}`)
          }
          if (change.allowance_mod && typeof change.allowance_mod === 'object') {
            const oldValue = change.allowance_mod.old_value ?? '<none>'
            const newValue = change.allowance_mod.new_value ?? '<none>'
            updates.push(`Allowance: ${oldValue} → ${newValue}`)
          }

          return (
            <ProgramUpdate key={idx}>
              <FieldName>✏️ {change.program_name}:</FieldName>
              {updates.map((update, uIdx) => (
                <div key={uIdx} style={{ marginLeft: '16px' }}>
                  {update}
                </div>
              ))}
            </ProgramUpdate>
          )
        })}
      </ChangesList>
    )
  }

  const renderMetadataChanges = (metadataChanges?: Record<string, UserFieldChange<any>>) => {
    if (!metadataChanges || Object.keys(metadataChanges).length === 0) {
      return '-'
    }

    return (
      <ChangesList>
        {Object.entries(metadataChanges).map(([field, change]) => (
          <ChangeItem key={field}>
            <FieldName>{field}:</FieldName>
            <ChangeValue>
              <OldValue>
                {change.old_value ?? <EmptyPlaceholder>&lt;empty&gt;</EmptyPlaceholder>}
              </OldValue>
              <NewValue>
                {change.new_value ?? <EmptyPlaceholder>&lt;empty&gt;</EmptyPlaceholder>}
              </NewValue>
            </ChangeValue>
          </ChangeItem>
        ))}
      </ChangesList>
    )
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <TheadTr>
            <Th>User</Th>
            <Th>User Field Changes</Th>
            <Th>Program Changes</Th>
            <Th>Metadata Changes</Th>
          </TheadTr>
        </Thead>
        <tbody>
          {users.map((user) => (
            <Tr key={user.user_id}>
              <Td>
                {user.first_name} {user.last_name}
                <div style={{ fontSize: '11px', color: towerGray, marginTop: '4px' }}>
                  {user.email}
                </div>
              </Td>
              <Td>{renderFieldChanges(user.user_field_changes)}</Td>
              <Td>{renderProgramChanges(user.program_changes)}</Td>
              <Td>{renderMetadataChanges(user.metadata_changes)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  )
}
